import { Check, Copy } from 'lucide-react';
import { useState } from 'react';

import { toast } from '@/components/ui/sonner.js';
import { Button } from '@/components/ui/button.js';
import { copyTextToClipboard } from '@/lib/copy.js';
import { cn } from '@/lib/utils.js';

interface CopyableValueProps {
  value: string;
  className?: string;
}

/** Monospace value with a one-click copy button (for UUIDs, paths, etc.). */
export function CopyableValue({ value, className }: CopyableValueProps) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    const ok = await copyTextToClipboard(value);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
      return;
    }
    toast.error('Could not copy to clipboard', {
      description: 'Select the value and copy manually (⌘C / Ctrl+C).',
    });
  };

  return (
    <div className={cn('flex min-w-0 items-start gap-1', className)}>
      <span className="min-w-0 flex-1 break-all font-mono text-xs">{value}</span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-7 w-7 shrink-0"
        onClick={() => void copy()}
        title="Copy to clipboard"
        aria-label="Copy to clipboard"
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 text-[var(--palette-success-dark)]" />
        ) : (
          <Copy className="h-3.5 w-3.5" />
        )}
      </Button>
    </div>
  );
}

interface FieldProps {
  label: string;
  value: string;
  mono?: boolean;
  copyable?: boolean;
}

export function MetadataField({ label, value, mono, copyable }: FieldProps) {
  const hasValue = value.length > 0;
  const display = hasValue ? value : '—';
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={cn('mt-0.5 break-words', !mono && !copyable && 'text-sm')}>
        {copyable && hasValue ? (
          <CopyableValue value={value} />
        ) : (
          <span className={cn(mono && hasValue && 'font-mono text-xs', !hasValue && 'text-muted-foreground')}>
            {display}
          </span>
        )}
      </div>
    </div>
  );
}
